import { translateDeep } from "@/lib/i18n-dict";
import type { Locale } from "@/lib/i18n";

export type FaqEntry = { q: string; a: string };

function faqJsonLd(faqs: readonly FaqEntry[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: { "@type": "Answer", text: faq.a },
    })),
  };
}

/**
 * Build a FAQPage JSON-LD script tag for a route `head()`.
 * Questions/answers are translated into the active locale so the structured
 * data matches the copy rendered on the page.
 */
export function faqScript(faqs: readonly FaqEntry[], locale: Locale) {
  if (!faqs.length) return [];
  const localised = translateDeep(locale, faqs as FaqEntry[]);
  return [
    {
      type: "application/ld+json",
      children: JSON.stringify(faqJsonLd(localised)),
    },
  ];
}

export function faqScriptFromEntries(faqs: readonly FaqEntry[]) {
  if (!faqs.length) return [];
  return [
    {
      type: "application/ld+json",
      children: JSON.stringify(faqJsonLd(faqs)),
    },
  ];
}

export function parseFaqEntriesFromMarkdown(markdown: string): FaqEntry[] {
  const faqStart = markdown.search(/^##\s+FAQ\s*$/im);
  if (faqStart < 0) return [];
  const faqBody = markdown.slice(faqStart).replace(/^##\s+FAQ\s*$/im, "").trim();
  const matches = [...faqBody.matchAll(/^###\s+(.+)$/gm)];
  const entries: FaqEntry[] = [];
  for (let index = 0; index < matches.length; index += 1) {
    const match = matches[index];
    const next = matches[index + 1];
    const question = match?.[1]?.trim();
    if (!match || match.index === undefined || !question) continue;
    const answerStart = match.index + match[0].length;
    const answerEnd = next?.index ?? faqBody.length;
    const answer = faqBody
      .slice(answerStart, answerEnd)
      .replace(/\n{2,}/g, " ")
      .replace(/[*_`#[\]]/g, "")
      .replace(/\((https?:[^)]+|\/[^)]+)\)/g, "")
      .trim();
    if (answer) entries.push({ q: question, a: answer });
  }
  return entries.slice(0, 12);
}
